import { useMemo } from 'react'

import type { ColumnDef } from '@tanstack/react-table'

import IconEdit from '@/assets/icons/icon-edit.svg'
import IconMore from '@/assets/icons/icon-menu-more.svg'
import IconPlus from '@/assets/icons/icon-plus.svg'
import IconClock from '@/assets/icons/icon-replay.svg'
import IconDelete from '@/assets/icons/icon-trash.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { DayOfMonth } from '@/modules/tasks/components/task-scheduler-dialog/day-of-month-selector.tsx'
import {
  rateToDayAndMonth,
  rateToDayOfMonth,
  rateToDaysOfWeek,
} from '@/modules/tasks/components/task-scheduler-dialog/task-scheduler-dialog.tsx'
import type { RepeatableTask } from '@/modules/tasks/service/repeatable-task-service.ts'
import { weekdays } from '@/modules/tasks/service/repeatable-task-service.ts'

interface DefineColumOptions {
  onEdit?: (task: RepeatableTask) => void
  onDelete?: (task: RepeatableTask) => void
}

const defineColumns = ({
  onEdit,
  onDelete,
}: DefineColumOptions): ColumnDef<RepeatableTask>[] => [
  {
    id: 'title',
    accessorKey: 'name',
    header: 'Name',
  },
  {
    id: 'title-rate-full',
    accessorKey: 'period',
    header: 'Repeats',
    cell: function Cell({ row }) {
      const rate = useMemo(() => {
        if (row.original.period === 'Daily') {
          return 'Every day'
        }
        if (row.original.period === 'Weekly') {
          const days = rateToDaysOfWeek(row.original.rate ?? [])

          if (days.length === weekdays.length) {
            return 'Every day'
          }

          return `Every ${days.map((day) => day.slice(0, 3)).join(', ')}`
        }
        if (row.original.period === 'Monthly') {
          const day = rateToDayOfMonth(row.original.rate ?? [])

          return (
            <>
              Every{' '}
              <strong>
                <DayOfMonth day={day} />
              </strong>{' '}
              day
            </>
          )
        }
        if (row.original.period === 'Annually') {
          const [day, month] = rateToDayAndMonth(row.original.rate ?? [])

          return (
            <>
              Every{' '}
              <strong>
                <DayOfMonth day={day} />
              </strong>{' '}
              of{' '}
              <strong>
                {new Date(0, month).toLocaleString('default', {
                  month: 'long',
                })}
              </strong>
            </>
          )
        }

        return null
      }, [row.original.period, row.original.rate])

      return (
        <span className="flex items-center gap-2">
          <IconClock className="h-4 w-4 flex-shrink-0 text-secondary-300" />
          {rate}
        </span>
      )
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <div className="float-right">
        <Popover>
          <PopoverTrigger>
            <IconMore className="h-5 w-5 text-title hover:text-secondary-300" />
          </PopoverTrigger>
          <PopoverContent className="w-[120px]">
            <div className="flex flex-col gap-2">
              <div
                className="flex cursor-pointer items-center gap-2 text-title transition duration-300 ease-out hover:text-primary-500"
                onClick={() => onEdit?.(row.original)}
              >
                <IconEdit className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">Edit</span>
              </div>
              <div
                className="flex cursor-pointer items-center gap-2 text-title transition duration-300 ease-out hover:text-danger-default"
                onClick={() => onDelete?.(row.original)}
              >
                <IconDelete className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">Delete</span>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    ),
  },
]

export interface TaskSchedulerTableProps {
  schedulers: RepeatableTask[]
  onCreate?: () => void
  onDelete?: (task: RepeatableTask) => void
  onEdit?: (task: RepeatableTask) => void
}

export const TaskSchedulerTable = (props: TaskSchedulerTableProps) => {
  const { schedulers, onCreate, onEdit, onDelete } = props

  const extra = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button
          className="col-span-6 gap-1 lg:col-span-3"
          onClick={onCreate}
        >
          <IconPlus className="h-5 w-5 flex-shrink-0" />
          New Scheduler
        </Button>
      </div>
    ),
    [onCreate],
  )

  const columns = useMemo(
    () => defineColumns({ onEdit, onDelete }),
    [onEdit, onDelete],
  )

  return (
    <DataTable
      title="Schedulers"
      extra={extra}
      columns={columns}
      data={schedulers}
    />
  )
}
